import type { CoreUser, CoreUserAttributes } from "~/types/auth"
import { userSessionState } from "@aschehoug/aschehoug-oidc-client"
import { useQuery } from "@tanstack/vue-query"
import { joinURL, withQuery } from "ufo"

export function useAuth() {
  const { terminateDeviceSession } = useDeviceLimiter()

  const redirect = useVippsRedirectPath()

  const userState = ref(userSessionState.loggedOut)

  const api = useApiClients()

  const client = useOidcClient()

  const { data: user } = useQuery({
    enabled: computed(() => userState.value === userSessionState.hasActiveSession),
    queryKey: ["user"],
    queryFn: () => api.external<CoreUser>("/rest/frontend/session/user"),
    select(coreUser) {
      return {
        firstName: coreUser.firstName,
        lastName: coreUser.lastName,
        fullName: coreUser.fullName,
        uid: coreUser.userId,
        email: coreUser.username,
      }
    },
  })

  async function init() {
    try {
      userState.value = await client.startAuthSession()

      const fromPath = redirect.value

      if (fromPath) {
        redirect.value = null
        await navigateTo(fromPath, { replace: true })
      }

      if (userState.value === userSessionState.hasActiveSession) {
        const userAttributes = await api.external<CoreUserAttributes>("/rest/frontend/session/attributes")
        return userAttributes.gaHash
      }
    } catch (e) {
      captureException(e)

      if (isUnauthorized(e)) {
        await logout()
      }
    }
  }

  async function logout(redirectPath = "") {
    try {
      await terminateDeviceSession(user.value?.uid)
      await client.logout()

      location.href = `${redirectPath ? "/produkter/" + redirectPath : "/"}`
    } catch (error) {
      captureException(error, {
        tags: {
          action: "logout",
        },
      })
    }
  }

  const { id } = useRuntimeConfig().public

  const origin = useCurrentOrigin()

  const loginUrl = computed(() => {
    const query: Record<string, string> = {
      returnUrl: withQuery(
        joinURL(id.baseUrl, "/am/XUI/"),
        {
          service: "vipps_kunne_oidc_tree",
          goto: joinURL(origin, "/am"),
          gotoOnFail: `${id.cloud}/feil`,
          realm: "/",
        },
      ),
    }

    const domain = id.baseUrl.replace("https://am.core", "")
    for (const cookie of ["am-frontend-sso", "am-sticky", "amlbcookie"]) {
      query[`cookies[${cookie}]`] = domain
    }

    return withQuery(
      joinURL(id.baseUrl, "/uf-auth-cookie-cleaner/delete-cookies"),
      query,
    )
  })

  async function login(fromPath?: string) {
    redirect.value = fromPath || "/"
    location.href = loginUrl.value
  }

  return {
    user,
    init,
    loginUrl,
    login,
    logout,
    isAuthorized: computed(() => !!user.value?.uid),
  }
}
